import type { WellIndexType, WidgetStateParams } from '@go-widgets/well-logs-widget';
import { BackLight, ExpandDownLight as ArrowIcon, DotsEditCustom as DotsIcon } from '@repo/go-icons';
import { Button, Tooltip } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DAY_DEPTH_AVAILABLE,
  DIRECTIONAL_DRILLING_AVAILABLE,
  DRILLING_MULTI_ANALYSIS_AVAILABLE,
  HEATMAP_AVAILABLE,
  IMAGE_BROADCAST_AVAILABLE,
  OPERATIONAL_PARAMETERS_AVAILABLE,
  VIDEOBROADCAST_AVAILABLE,
  WELL_ANALYSE_AVAILABLE,
  WELL_DETAILS_AVAILABLE,
} from 'src/api/consts';
import i18nInstance from 'src/app/i18n/i18n';
import type { TCreateWidgetOptions } from 'src/entities/tab/TabEntity';
import { requireService } from 'src/packages/di';

import type { TTabTemplateWithGSProps } from '../workspace/types';

import styles from './ContextMenuButton.module.scss';

type Props = {
  id: number;
  options?: TCreateWidgetOptions;
  onCreateWellLogsWidget?(
    wellIndexType: WellIndexType,
    wellId: number | null,
    stateParams: Partial<WidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): void;
  onCreateOperationalParamsWidget?(
    wellIndexType: WellIndexType,
    wellId: number | null,
    enableSound: boolean,
    options?: TCreateWidgetOptions,
  ): void;
  onCreateWellAnalyseWidget?(wellId: number | null, options?: TCreateWidgetOptions): void;
  onCreateDirectionalDrillingWidget?(wellId: number | null, options?: TCreateWidgetOptions): void;
  onCreateImageBroadcastWidget?(wellId: number | null, options?: TCreateWidgetOptions): void;
  onCreateVideoBroadcastWidget?(wellId: number | null, options?: TCreateWidgetOptions): void;
  onCreateDayDepthChartWidget?(wellId: number | null, options?: TCreateWidgetOptions): void;
  onCreateHeatmapWidget?(wellId: number | null, options?: TCreateWidgetOptions): void;
  onCreateDrillingMultiAnalysisWidget?(wellId: number | null, options?: TCreateWidgetOptions): void;
  onCreateTemplate?(template: TTabTemplateWithGSProps, wellId: number | null): void;
};

export const ContextMenuButton = observer(function ContextMenuButton({
  id,
  options,
  onCreateWellLogsWidget,
  onCreateTemplate,
  onCreateOperationalParamsWidget,
  onCreateWellAnalyseWidget,
  onCreateDirectionalDrillingWidget,
  onCreateImageBroadcastWidget,
  onCreateVideoBroadcastWidget,
  onCreateDayDepthChartWidget,
  onCreateHeatmapWidget,
  onCreateDrillingMultiAnalysisWidget,
}: Props) {
  const [isOpen, setOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'main' | 'widgets' | 'templates'>('main');
  const [isSensorsParamsExpanded, setIsSensorsParamsExpanded] = useState(true);
  const [isOperationalParamsExpanded, setIsOperationalParamsExpanded] = useState(true);
  const [expandedTemplateGroup, setExpandedTemplateGroup] = useState<'system' | 'public' | 'personal' | null>(null);

  const { t } = useTranslation('wellList', { i18n: i18nInstance });
  const tabTemplatesService = requireService('tabTemplates');

  const onCreateWelLogsByTime = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateWellLogsWidget?.('LOG_TIME', id, null, options);
  };

  const onCreateWelLogsByDepth = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateWellLogsWidget?.('LOG_DEPTH', id, null, options);
  };

  const onCreateOperationalParamsByTime = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateOperationalParamsWidget?.('LOG_TIME', id, false, options);
  };

  const onCreateOperationalParamsByDepth = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateOperationalParamsWidget?.('LOG_DEPTH', id, false, options);
  };

  const onCreateWellAnalyse = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateWellAnalyseWidget?.(id, options);
  };

  const onCreateDirectionalDrilling = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateDirectionalDrillingWidget?.(id, options);
  };

  const onCreateImageBroadcast = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateImageBroadcastWidget?.(id, options);
  };

  const onCreateVideoBroadcast = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateVideoBroadcastWidget?.(id, options);
  };

  const onCreateDayDepthChart = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateDayDepthChartWidget?.(id, options);
  };

  const onCreateHeatmap = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateHeatmapWidget?.(id, options);
  };

  const onCreateDrillingMultiAnalysis = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateDrillingMultiAnalysisWidget?.(id, options);
  };

  // const onSystemTemplatesCollapseClick = (e: MouseEvent) => {
  //   e.stopPropagation();
  //   setExpandedTemplateGroup(expandedTemplateGroup === 'system' ? null : 'system');
  // };

  const onPublicTemplatesCollapseClick = (e: MouseEvent) => {
    e.stopPropagation();
    setExpandedTemplateGroup(expandedTemplateGroup === 'public' ? null : 'public');
  };

  const onPersonalTemplatesCollapseClick = (e: MouseEvent) => {
    e.stopPropagation();
    setExpandedTemplateGroup(expandedTemplateGroup === 'personal' ? null : 'personal');
  };

  const onTemplateClick = (template: TTabTemplateWithGSProps) => (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onCreateTemplate?.(template, id);
  };

  const onBackClick = (e: MouseEvent) => {
    e.stopPropagation();
    setCurrentView('main');
    setIsSensorsParamsExpanded(true);
    setIsOperationalParamsExpanded(true);
    setExpandedTemplateGroup(null);
  };

  const onWidgetsClick = (e: MouseEvent) => {
    e.stopPropagation();
    setCurrentView('widgets');
  };

  const onTemplatesClick = (e: MouseEvent) => {
    e.stopPropagation();
    setCurrentView('templates');
  };

  const hasAvailableWidgets =
    !!onCreateWellLogsWidget ||
    !!onCreateOperationalParamsWidget ||
    !!onCreateWellAnalyseWidget ||
    !!onCreateDirectionalDrillingWidget ||
    !!onCreateImageBroadcastWidget ||
    !!onCreateVideoBroadcastWidget ||
    !!onCreateDayDepthChartWidget;

  if (!hasAvailableWidgets && !onCreateTemplate) {
    return null;
  }

  const content = (
    <div className={styles.wrapper}>
      {currentView === 'main' && (
        <>
          {hasAvailableWidgets && (
            <button type="button" className={styles.button} onClick={onWidgetsClick}>
              {t('contextMenu.widgets')}
            </button>
          )}
          {!!onCreateTemplate && (
            <button type="button" className={styles.button} onClick={onTemplatesClick}>
              {t('contextMenu.templates')}
            </button>
          )}
        </>
      )}

      {currentView === 'widgets' && (
        <>
          <Button variant="flat" icon={<BackLight />} className={styles.backButton} onClick={onBackClick}>
            {t('contextMenu.widgets')}
          </Button>
          {!!onCreateWellLogsWidget && (
            <>
              <Button
                variant="flat"
                className={clsx(styles.button, styles.collapseButton)}
                icon={<ArrowIcon className={clsx(styles.arrowIcon, !isSensorsParamsExpanded && styles.arrowIconUp)} />}
                onClick={(e) => setIsSensorsParamsExpanded(!isSensorsParamsExpanded)}
              >
                {t('contextMenu.sensorsParams')}
              </Button>
              {isSensorsParamsExpanded && (
                <div className={styles.collapseContent}>
                  <button
                    type="button"
                    className={clsx(styles.collapseContentButton, styles.button)}
                    onClick={onCreateWelLogsByTime}
                  >
                    {t('contextMenu.commonByTime')}
                  </button>
                  <button
                    type="button"
                    className={clsx(styles.collapseContentButton, styles.button)}
                    onClick={onCreateWelLogsByDepth}
                  >
                    {t('contextMenu.commonByDepth')}
                  </button>
                </div>
              )}
            </>
          )}

          {OPERATIONAL_PARAMETERS_AVAILABLE && !!onCreateOperationalParamsWidget && (
            <>
              <Button
                variant="flat"
                className={clsx(styles.button, styles.collapseButton)}
                icon={
                  <ArrowIcon className={clsx(styles.arrowIcon, !isOperationalParamsExpanded && styles.arrowIconUp)} />
                }
                onClick={(e) => setIsOperationalParamsExpanded(!isOperationalParamsExpanded)}
              >
                {t('contextMenu.operationalParams')}
              </Button>
              {isOperationalParamsExpanded && (
                <div className={styles.collapseContent}>
                  <button
                    type="button"
                    className={clsx(styles.collapseContentButton, styles.button)}
                    onClick={onCreateOperationalParamsByTime}
                  >
                    {t('contextMenu.operationalByTime')}
                  </button>
                  <button
                    type="button"
                    className={clsx(styles.collapseContentButton, styles.button)}
                    onClick={onCreateOperationalParamsByDepth}
                  >
                    {t('contextMenu.operationalByDepth')}
                  </button>
                </div>
              )}
            </>
          )}

          {WELL_ANALYSE_AVAILABLE && !!onCreateWellAnalyseWidget && (
            <button type="button" className={styles.button} onClick={onCreateWellAnalyse}>
              {t('contextMenu.wellAnalyse')}
            </button>
          )}

          {DIRECTIONAL_DRILLING_AVAILABLE && !!onCreateDirectionalDrillingWidget && (
            <button type="button" className={styles.button} onClick={onCreateDirectionalDrilling}>
              {t('contextMenu.directionalDrilling')}
            </button>
          )}

          {IMAGE_BROADCAST_AVAILABLE && !!onCreateImageBroadcastWidget && (
            <button type="button" className={styles.button} onClick={onCreateImageBroadcast}>
              {t('contextMenu.imageBroadcast')}
            </button>
          )}

          {VIDEOBROADCAST_AVAILABLE && !!onCreateVideoBroadcastWidget && (
            <button type="button" className={styles.button} onClick={onCreateVideoBroadcast}>
              {t('contextMenu.videoBroadcast')}
            </button>
          )}

          {DAY_DEPTH_AVAILABLE && !!onCreateDayDepthChartWidget && (
            <button type="button" className={styles.button} onClick={onCreateDayDepthChart}>
              {t('contextMenu.dayDepthChart')}
            </button>
          )}

          {WELL_DETAILS_AVAILABLE && (
            <button type="button" className={styles.button} onClick={(e) => e.stopPropagation()}>
              {t('contextMenu.wellData')}
            </button>
          )}
          {HEATMAP_AVAILABLE && !!onCreateHeatmapWidget && (
            <button type="button" className={styles.button} onClick={onCreateHeatmap}>
              {t('contextMenu.heatmap')}
            </button>
          )}
          {DRILLING_MULTI_ANALYSIS_AVAILABLE && !!onCreateDrillingMultiAnalysisWidget && (
            <button type="button" className={styles.button} onClick={onCreateDrillingMultiAnalysis}>
              {t('contextMenu.drillingMultiAnalysis')}
            </button>
          )}
        </>
      )}

      {currentView === 'templates' && (
        <>
          <Button variant="flat" icon={<BackLight />} className={styles.backButton} onClick={onBackClick}>
            {t('contextMenu.templates')}
          </Button>

          {/* {tabTemplatesService.system.length > 0 && (
            <>
              <Button
                variant="flat"
                className={clsx(styles.collapseContentButton, styles.button, styles.collapseButton)}
                icon={
                  <ArrowIcon
                    className={clsx(styles.arrowIcon, expandedTemplateGroup !== 'system' && styles.arrowIconUp)}
                  />
                }
                onClick={onSystemTemplatesCollapseClick}
              >
                {t('contextMenu.systemTemplates')}
              </Button>
              {expandedTemplateGroup === 'system' && (
                <div className={styles.collapseContent}>
                  {tabTemplatesService.system
                    .slice()
                    .sort((a, b) => a.data.template.name.localeCompare(b.data.template.name))
                    .map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        className={clsx(styles.collapseContentButton, styles.button)}
                        onClick={onTemplateClick(template)}
                      >
                        {template.data.template.name}
                      </button>
                    ))}
                </div>
              )}
            </>
          )} */}

          {tabTemplatesService.publicTemplates.length > 0 && (
            <>
              <Button
                variant="flat"
                className={clsx(styles.collapseContentButton, styles.button, styles.collapseButton)}
                icon={
                  <ArrowIcon
                    className={clsx(styles.arrowIcon, expandedTemplateGroup !== 'public' && styles.arrowIconUp)}
                  />
                }
                onClick={onPublicTemplatesCollapseClick}
              >
                {t('contextMenu.publicTemplates')}
              </Button>
              {expandedTemplateGroup === 'public' && (
                <div className={styles.collapseContent}>
                  {tabTemplatesService.publicTemplates
                    .slice()
                    .sort((a, b) => a.data.template.name.localeCompare(b.data.template.name))
                    .map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        className={clsx(styles.collapseContentButton, styles.button)}
                        onClick={onTemplateClick(template)}
                      >
                        {template.data.template.name}
                      </button>
                    ))}
                </div>
              )}
            </>
          )}

          {tabTemplatesService.personal.length > 0 && (
            <>
              <Button
                variant="flat"
                className={clsx(styles.collapseContentButton, styles.button, styles.collapseButton)}
                icon={
                  <ArrowIcon
                    className={clsx(styles.arrowIcon, expandedTemplateGroup !== 'personal' && styles.arrowIconUp)}
                  />
                }
                onClick={onPersonalTemplatesCollapseClick}
              >
                {t('contextMenu.personalTemplates')}
              </Button>
              {expandedTemplateGroup === 'personal' && (
                <div className={styles.collapseContent}>
                  {tabTemplatesService.personal
                    .slice()
                    .sort((a, b) => a.data.template.name.localeCompare(b.data.template.name))
                    .map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        className={clsx(styles.collapseContentButton, styles.button)}
                        onClick={onTemplateClick(template)}
                      >
                        {template.data.template.name}
                      </button>
                    ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <Tooltip
      placement="bottom-end"
      withArrow={false}
      title={content}
      className={styles.popover}
      onOpenChange={setOpen}
      open={isOpen}
      trigger={['click']}
    >
      <Button
        icon={<DotsIcon className={clsx(styles.icon, isOpen && styles.icon__active)} />}
        variant="flat"
        className={styles.iconButton}
      />
    </Tooltip>
  );
});
